import {FormikContextType} from 'formik';
import {useEffect, useState} from 'react';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import {ToasterService, ToastType} from '~commonInfra/services/ToasterService';
import {SessionServerSelectors} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import {Messages} from '~src/codegen/Localize';
import {OAuthCredentialTypes} from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import {AddOAuthCredentialAction} from '~src/connectivity/services/AddOAuthCredentialAction';
import {makeJsonRestApiRequest} from '~src/httpClient/RestApiService';
import {
  GenerativeAISectionProps,
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAISection';
import {useSelector} from '~src/settings/general-settings/utils';

export interface GenerativeAiRegistrationState {
  registered: boolean;
  domain?: string;
  username?: string;
  orgId?: string;
}

interface GaiResponse {
  generativeAiRegistration: GenerativeAiRegistrationState;
}

export type GenerativeAIRegistrationHookType = {
  registrationState: GenerativeAiRegistrationState;
  unregisterEnterprise: () => Promise<void>;
  registerEnterprise: () => Promise<void>;
}

export function useGenerativeAIRegistrationHook(formik: FormikContextType<GenerativeAISectionProps>): GenerativeAIRegistrationHookType {

  const [registrationState, setRegistrationState]
    = useState<GenerativeAiRegistrationState>({registered: false});

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);

  const requestState= FeatureFlags.tabGAITokenService &&
                            FeatureFlags.tabGAITokenServiceEnterpriseRegistration && isOnlineEnvironment;

  useEffect(() => {
    // We only request the state when the FF is enabled
    if(requestState) {
      makeJsonRestApiRequest<void, GaiResponse>(
        {
          config: {
            restApiVersion: '3.24',
            httpMethod: 'GET',
            endpoint: `gai/registration`,
            serverWideRequest: false
          }, request: null, queryParams: null
        }
      ).then(
        x => {
          setRegistrationState(x.generativeAiRegistration);
        },
        (reason: any) => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.tableauGAISiteSettingsReadError()
          });
        });
    }
  }, [requestState]);

  async function unregisterEnterprise(): Promise<void> {
    makeJsonRestApiRequest<void, void>(
      {config: {
          restApiVersion: '3.24',
          httpMethod: 'DELETE',
          endpoint: `gai/registration`,
          serverWideRequest: false
        }, request: null, queryParams: null }
    ).then(
      _ => {
        setRegistrationState({registered:false});
        if(FeatureFlags.tabGAITokenServiceRegistration) {
          formik.resetForm({
            values: {
              ...formik.values,
              settings: {
                ...formik.values.settings,
                generativeAiWebAuthoring: false,
                generativeAiPrep: false,
                generativeAiPulse: false,
                generativeAiDataCatalog: false
          }}});
        }
      },
      (reason: any) => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.tableauGAISiteSettingsUnregisterError()});
      });
  }

  async function registerEnterprise(): Promise<void> {
    return AddOAuthCredentialAction.attemptAuth(OAuthCredentialTypes.Salesforce, null, null, null, null, /* requiresUpgrade: */ true)
      .then((credentialId) => {
          makeJsonRestApiRequest<void, GaiResponse>({
            config: {
              restApiVersion: '3.24',
              httpMethod: 'PUT',
              endpoint: `gai/registration`,
              serverWideRequest: false
            }, request: null, queryParams: {id: credentialId} }
          ).then(x => {
            setRegistrationState(x.generativeAiRegistration);
            },
            (reason: any) => {
              ToasterService.instance.pop({
                type: ToastType.Error,
                text: Messages.tableauGAISiteSettingsRegisterError()});
            }
          );
        },
        (reason: any) => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.tableauGAISiteSettingsRegisterError()});
        }
      );
  }

  return {registrationState, unregisterEnterprise, registerEnterprise};
}
